import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ProposalForm from './ProposalForm/ProposalForm';
import ProjectSalesTaxWarningModal from '../ProjectSalesTaxWarningModal';
import API_BASE from '../../../utils/api.js';
import { hasProjectSalesTaxAssigned } from '../../../utils/projectSalesTax.js';
import usePermissions from '../../../auth/usePermissions';

export default function ProposalFormPage() {
  const { projectId, proposalId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions, permissionsLoaded } = usePermissions();

  useEffect(() => {
    if (permissionsLoaded && !proposalId && permissions.createEditInvoicesProposals === false) {
      navigate(`/projects/${projectId}`, { replace: true });
    }
  }, [permissionsLoaded, permissions, projectId, proposalId, navigate]);

  const passedProject = location.state?.project || null;
  const initial = location.state?.proposal || null;

  const [project, setProject] = useState(passedProject);
  const [fetchedProposal, setFetchedProposal] = useState(null);
  const [error, setError] = useState(null);
  const taxAlertShownRef = useRef(false);
  const [showTaxWarning, setShowTaxWarning] = useState(false);

  useEffect(() => {
    if (project || !projectId) return;

    (async () => {
      try {
        const userId = localStorage.getItem('userId');

        if (!userId) {
          setError('Missing userId in localStorage');
          return;
        }

        const res = await fetch(
          `${API_BASE}/api/projects/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`
        );

        if (!res.ok) {
          const msg = `Failed to fetch project (status ${res.status})`;
          setError(msg);
          return;
        }

        const data = await res.json();
        setProject(data || null);
      } catch (e) {
        setError("This page couldn't be loaded. Please refresh the page or return to the project.");
      }
    })();
  }, [project, projectId]);

  useEffect(() => {
    if (proposalId && !initial) {
      (async () => {
        try {
          const apiBase = API_BASE.replace(/\/$/, '');
          const userId = localStorage.getItem('userId');

          if (!userId) {
            setError('Missing userId in localStorage');
            return;
          }

          const url = `${apiBase}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}/${encodeURIComponent(proposalId)}`;
          const res = await fetch(url);

          if (!res.ok) {
            const msg = `Failed to fetch proposal (status ${res.status})`;
            setError(msg);
            return;
          }

          const data = await res.json();
          setFetchedProposal(data);
        } catch (e) {
          setError("This proposal couldn't be loaded. Please refresh the page or return to the project.");
        }
      })();
    }
  }, [proposalId, initial, projectId]);

  useEffect(() => {
    if (proposalId || initial || !project || taxAlertShownRef.current) return;
    taxAlertShownRef.current = true;
    if (!hasProjectSalesTaxAssigned(project)) {
      setShowTaxWarning(true);
    }
  }, [proposalId, initial, project]);

  return (
    <div className="proposal-page">
      {error && (
        <div style={{ padding: '12px', color: 'crimson' }}>Error loading proposal: {error}</div>
      )}

      <ProposalForm
        project={project}
        initial={initial || fetchedProposal}
        depositToRecord={location.state?.recordDeposit || null}
        onClose={() => navigate(`/projects/${projectId}/proposals`)}
        onSaved={() => navigate(`/projects/${projectId}/proposals`)}
      />

      <ProjectSalesTaxWarningModal
        isOpen={showTaxWarning}
        docLabel="proposal"
        onClose={() => setShowTaxWarning(false)}
        onEditProject={() =>
          navigate(`/projects/${encodeURIComponent(projectId)}/edit`, {
            state: { project },
          })
        }
      />
    </div>
  );
}
